/**
 * Applies the HUD scale and motion preference to the document root:
 *  --ui-zoom = automatic resolution factor × the player's uiScale setting.
 * The automatic factor keeps the HUD proportionate from 1280×720 up to 4K without the player
 * having to touch anything (base design ≈ 1600×900 CSS px).
 */
import { useEffect } from 'react';
import { useSettings } from './settings';

export function autoZoom(w: number, h: number): number {
  return Math.max(0.86, Math.min(1.6, Math.min(w / 1600, h / 900)));
}

export function useUiScale(): void {
  const uiScale = useSettings((s) => s.uiScale);
  const reduced = useSettings((s) => s.reducedMotion);

  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const z = autoZoom(window.innerWidth, window.innerHeight) * uiScale;
      root.style.setProperty('--ui-zoom', z.toFixed(3));
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [uiScale]);

  useEffect(() => {
    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      document.documentElement.dataset.reducedMotion = String(reduced || mq.matches);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [reduced]);
}
